import React from 'react';
import { CheckCircle2, FileWarning, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ValidateStep({ validation, errorCount, warningCount }) {
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-4 rounded-xl border border-slate-200 bg-white">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Total Rows</div>
          <div className="text-2xl font-bold text-slate-900">{validation.objects.length}</div>
        </div>
        <div className={cn('p-4 rounded-xl border', errorCount > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200')}>
          <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: errorCount > 0 ? '#991B1B' : '#065F46' }}>Errors</div>
          <div className={cn('text-2xl font-bold', errorCount > 0 ? 'text-[#CC0000]' : 'text-emerald-600')}>{errorCount}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Must fix before saving</div>
        </div>
        <div className={cn('p-4 rounded-xl border', warningCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200')}>
          <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: warningCount > 0 ? '#92400E' : '#065F46' }}>Warnings</div>
          <div className={cn('text-2xl font-bold', warningCount > 0 ? 'text-amber-600' : 'text-emerald-600')}>{warningCount}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Can proceed with caution</div>
        </div>
      </div>

      {validation.issues.length === 0 ? (
        <div className="p-6 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
          <div className="text-sm font-bold text-emerald-900">All validation checks passed</div>
          <p className="text-xs text-emerald-800 mt-1">No errors or warnings found. Ready to save and generate JSON schema.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-700">Validation issues</span>
            <span className="text-[10px] text-slate-500">Showing {validation.issues.length} {validation.issues.length === 1 ? 'issue' : 'issues'}</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
            {validation.issues.map((iss, i) => (
              <div key={`iss-${iss.severity || 's'}-${iss.row || i}-${i}`} className="px-4 py-2.5 flex items-start gap-3 hover:bg-slate-50/60">
                {iss.severity === 'error' ? (
                  <FileWarning className="w-4 h-4 text-[#CC0000] flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="text-xs font-medium text-slate-900">{iss.message}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 font-mono">type: {iss.type}{iss.row ? ` · row: ${iss.row}` : ''}</div>
                </div>
                <span className={cn(
                  'text-[9px] font-bold uppercase px-1.5 py-0.5 rounded',
                  iss.severity === 'error' ? 'bg-red-100 text-[#CC0000]' : 'bg-amber-100 text-amber-700'
                )}>
                  {iss.severity}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
